<template>
  <div class="min-h-screen">
    <!-- Stepper -->
    <div class="bg-white p-4 shadow-md flex justify-center">
      <div class="w-full max-w-2xl">
        <div class="flex justify-center items-center">
          <div v-for="(step, index) in steps" :key="index" class="flex items-center flex-1">
            <div :class="[
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
              currentStep > index ? 'bg-custom-yellow text-black' : 'bg-gray-300 text-gray-600'
            ]">
              {{ index + 1 }}
            </div>
            <div v-if="index < steps.length - 1" class="h-1 flex-1 bg-gray-300 mx-2">
              <div :class="[
                'h-full',
                currentStep > index ? 'bg-custom-yellow' : 'bg-gray-300'
              ]" :style="{ width: `${(currentStep > index ? 100 : 0)}%` }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div v-if="showPatientPopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div class="bg-white p-4 sm:p-6 shadow-xl w-full max-w-[95%] sm:max-w-[85%] md:max-w-[75%] lg:w-8/12 xl:w-6/12 
                rounded-2xl flex flex-col min-h-[300px] max-h-[90vh] sm:max-h-[80vh]">
        <h3 class="text-lg font-bold mb-4">Select Patient</h3>

        <div class="flex-grow overflow-y-auto">
          <div class="space-y-2">
            <template v-if="matchingPatients.length > 0">
              <div v-for="patient in matchingPatients" :key="patient.id"
                class="grid grid-cols-1 sm:grid-cols-12 gap-y-2 sm:gap-y-0 items-start sm:items-center p-3 bg-gray-50 rounded-lg">
                <div class="col-span-1 sm:col-span-5 space-y-1">
                  <div class="text-sm text-gray-600">Name:</div>
                  <div class="font-medium">{{ patient.form.fullName }}</div>
                </div>

                <div class="col-span-1 sm:col-span-5 space-y-1">
                  <div class="text-sm text-gray-600">Relationship:</div>
                  <div class="font-medium">{{ patient.form.relationship }}</div>
                </div>

                <div class="col-span-1 sm:col-span-2 flex justify-start sm:justify-end mt-2 sm:mt-0">
                  <button @click="selectPatient(patient)" class="w-full sm:w-auto px-4 py-2 bg-custom-blue text-white rounded hover:bg-blue-800 
                         transition-colors text-sm">
                    Select
                  </button>
                </div>
              </div>
            </template>
            <div v-else class="text-center py-4 text-gray-500">
              No matching patients found.
            </div>
          </div>
        </div>

        <div class="mt-4 sm:mt-6 flex justify-end">
          <button @click="showPatientPopup = false" class="w-full sm:w-auto px-6 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200 
                 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>


    <!-- Form content -->
    <div class="p-4">
      <h2 class="text-xl font-bold mb-4">{{ steps[currentStep - 1] }}</h2>

      <!-- Personal Information -->
      <div v-if="currentStep === 1" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700">ID Number/Passport Number <span
              class="text-red-500">*</span></label>
          <input v-model="form.idNumber" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Phone Number <span
              class="text-red-500">*</span></label>
          <input v-model="form.phoneNumber" type="text" @input="debouncedLookupPatient"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Title <span class="text-red-500">*</span></label>
          <select v-model="form.title"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">Mr/Mrs/Miss/Madam</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Miss">Miss</option>
            <option value="Madam">Madam</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Full Name <span class="text-red-500">*</span></label>
          <input v-model="form.fullName" type="text" @input="debouncedLookupPatient"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">D.O.B <span class="text-red-500">*</span></label>
          <input v-model="form.dob" type="date"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Age <span class="text-red-500">*</span></label>
          <input v-model="form.age" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            readonly>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Gender <span class="text-red-500">*</span></label>
          <select v-model="form.gender"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">Select (Female,Male)</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Relationship <span
              class="text-red-500">*</span></label>
          <select v-model="form.relationship"
            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">Principal Owner/Dependent</option>
            <option value="Male">Principal Owner</option>
            <option value="Female">Dependent</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Visit Type <span class="text-red-500">*</span></label>
          <select v-model="form.visitType"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">Consultation/Triage Procedure</option>
            <option value="Consultation">Consultation</option>
            <option value="Triage">Triage</option>
            <option value="Procedure">Procedure</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Blood Group <span class="text-red-500">*</span></label>
          <select v-model="form.bloodGroup"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">A+,A-,AB+,AB-,O-,O+</option>
            <option value="A+">A+</option>
            <option value="A-">A-</option>
            <option value="AB+">AB+</option>
            <option value="AB-">AB-</option>
            <option value="O-">O-</option>
            <option value="O+">O+</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700">Email Address <span
              class="text-red-500">*</span></label>
          <input v-model="form.emailAddress" type="email"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Occupation <span class="text-red-500">*</span></label>
          <input v-model="form.occupation" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Marital Status <span
              class="text-red-500">*</span></label>
          <select v-model="form.maritalStatus"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">eg Single,Married,Widowed...</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Widowed">Widowed</option>
            <option value="Divorced">Divorced</option>
            <option value="Separated">Separated</option>
            <option value="Registered Partnerships">Registered Partnerships</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Residence <span class="text-red-500">*</span></label>
          <input v-model="form.residence" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...eg Kenyan">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">County <span class="text-red-500">*</span></label>
          <select v-model="form.county"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
            <option value="">Select (Drop down of all 47 counties)</option>
            <!-- Add options for all 47 counties here -->
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Nationality <span class="text-red-500">*</span></label>
          <input v-model="form.nationality" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...eg Kenyan">
        </div>
      </div>

      <!-- Emergency Contact -->
      <div v-if="currentStep === 2" class="space-y-4">
        <div class="flex justify-end pl-4 ml-auto">
          <button @click="addEmergencyContact"
            class="text-custom-yellow hover:text-custom-yellow-dark border-4 border-custom-yellow  p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Full Name <span class="text-red-500">*</span></label>
          <input v-model="emergencyContact.fullName" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Enter Full Name">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Relationship <span
              class="text-red-500">*</span></label>
          <input v-model="emergencyContact.relationship" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Phone Number <span
              class="text-red-500">*</span></label>
          <input v-model="emergencyContact.phoneNumber" type="text"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50"
            placeholder="Type...">
        </div>

        <!-- Display Added Emergency Contacts in Table Format -->
        <div v-if="emergencyContacts.length" class="mt-6">
          <h3 class="text-lg font-bold text-gray-800 mb-4">Added Emergency Contacts</h3>
          <table class="w-full bg-white rounded-md shadow-md">
            <thead>
              <tr class="bg-gray-100 border-b">
                <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Full Name</th>
                <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Relationship</th>
                <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Phone Number</th>
                <th class="py-2 px-4 text-left text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(contact, index) in emergencyContacts" :key="index" class="border-b">
                <td class="py-2 px-4">{{ contact.fullName }}</td>
                <td class="py-2 px-4">{{ contact.relationship }}</td>
                <td class="py-2 px-4">{{ contact.phoneNumber }}</td>
                <td class="py-2 px-4 space-x-2">
                  <button @click="deleteEmergencyContact(index)"
                    class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50">
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Payment Information -->
      <div v-if="currentStep === 3" class="space-y-6 flex flex-col justify-center items-center mt-6">
        <div class="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h2 class="text-gray-600 font-medium mb-4">Select Payment Method</h2>
          <div class="flex justify-between mb-6">
            <button v-for="method in paymentMethods" :key="method.id"
              class="flex flex-col items-center justify-center p-4 rounded-lg relative" :class="[
                selectedMethod === method.id
                  ? 'bg-blue-50 border border-blue-500'
                  : 'bg-gray-50'
              ]" @click="selectedMethod = method.id">
              <img class="object-cover w-12 h-12 rounded-full" :src="method.url" alt="Your avatar" />
              <span class="text-gray-400">{{ method.name }}</span>
              <span v-if="selectedMethod === method.id"
                class="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <span class="text-white text-xs">✓</span>
              </span>
            </button>
          </div>

          <div v-if="selectedMethod === 'walkin'" class="mt-6">
            <h3 class="text-gray-600 font-medium mb-2">Select Mobile Money</h3>
            <div class="relative">
              <select v-model="selectedMobileProvider"
                class="w-full p-2 pr-8 border border-gray-300 rounded-md appearance-none bg-white text-gray-400">
                <option>Safaricom</option>
                <option>Airtel</option>
                <option>Other</option>
              </select>
              <span class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                ▼
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload/Capture Referral Letter -->
      <div v-if="currentStep === 4" class="space-y-4">
        <div class="rounded-lg p-4 w-1/2 mx-auto">
          <div class="flex flex-wrap gap-4">
            <!-- File upload area -->
            <div
              class="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center w-48 h-48 cursor-pointer"
              @click="triggerFileInput" @dragover.prevent @drop.prevent="handleFileDrop">
              <input type="file" ref="fileInput" class="hidden" @change="handleFileSelect" accept="image/*,.pdf">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400 mb-2" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p class="text-sm text-gray-500">Drag and drop your files here</p>
              <p class="text-sm text-gray-500">OR</p>
              <button
                class="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50">
                Browse files
              </button>
            </div>

            <!-- Uploaded document previews -->
            <div v-for="(doc, index) in uploadedDocuments" :key="index" class="relative">
              <img :src="doc.preview" alt="Uploaded document" class="w-48 h-48 object-cover rounded-lg">
              <button @click="removeDocument(index)"
                class="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>


      <div v-if="currentStep === 5" class="bg-white p-6 rounded-lg shadow-md">
        <div class="bg-orange-50 border-l-4 border-custom-yellow p-4 mb-4">
          <h3 class="font-bold mb-2">WHAT DO WE MEAN BY PATIENT CONSENT FORM</h3>
          <ul class="list-disc pl-5 space-y-2">
            <li>Patient Consent is for Data Collection, Processing, Retention, Treatment and Financial Responsibility.
            </li>
            <li>By signing this form, you, as the patient (or legally authorized representative), give your informed
              consent for the Following:</li>
          </ul>
        </div>

        <p class="mb-4">As part of your care, the Healthcare Facility/Provider will collect, process and retain certain
          personal, medical and financial information. This includes but is not limited to:</p>

        <ul class="list-disc pl-5 space-y-2 mb-4">
          <li><span class="font-semibold">Personal Information:</span> Name, date of birth, contact information,
            identification numbers and insurance details.</li>
          <li><span class="font-semibold">Medical Information:</span> Medical history, diagnostic results, treatment
            records and other health-related data required for your care.</li>
          <li><span class="font-semibold">Financial Information:</span> Billing information, payment records and
            insurance claim details.</li>
        </ul>

        <h3 class="font-bold mb-2">Purpose of Data Collection:</h3>
        <ul class="list-disc pl-5 space-y-2 mb-4">
          <li>To provide medical diagnosis, treatment and ongoing care</li>
          <li>To comply with legal, regulatory and insurance requirements.</li>
          <li>For Administrative, billing and payment purposes.</li>
        </ul>

        <h3 class="font-bold mb-2">Data Processing and Retention:</h3>
        <ol class="list-decimal pl-5 space-y-2 mb-4">
          <li>Your data will be processed and stored securely in compliance with relevant Kenyan laws, including the
            Data Protection Act 2019 of Kenya.</li>
          <li>Your data may be shared with third-party providers, insurance companies, or regulatory authorities as
            necessary for treatment, payment, or healthcare operations.</li>
          <li>Your information will be retained for as long as required by law or necessary for the purposes described
            above.</li>
          <li>You have the right to withdraw consent for data collection and processing at any time, subject to legal
            obligations and the ability to provide appropriate medical care.</li>
        </ol>

        <div class="flex items-center space-x-4 mb-4 border-t-2 py-6">
          <input type="checkbox" id="consentChecked" v-model="agreements.consentChecked"
            class="form-checkbox h-5 w-5 text-custom-yellow border-2 border-custom-yellow focus:ring-custom-yellow">
          <label for="consentChecked" class="text-sm font-semibold">
            Consent for Data Collection, Processing and Retention.
          </label>
        </div>

        <!-- Consent for Treatment -->
        <h2 class="text-2xl font-bold mb-4">Consent for Treatment</h2>
        <p class="mb-4">By signing below, you provide informed consent for the following:</p>
        <ul class="list-disc pl-5 space-y-2 mb-6">
          <li><span class="font-semibold">Medical Treatment:</span> You authorize the medical facility/provider to
            provide necessary medical treatment, including diagnostic tests, therapies, procedures, and medication
            administration, as deemed appropriate by the healthcare provider.</li>
          <li><span class="font-semibold">Alternative Treatments:</span> You acknowledge that you have been informed of
            alternative treatment options, if applicable, and the risks, benefits, and possible outcomes of the
            recommended treatment.</li>
          <li><span class="font-semibold">Right to Refuse Treatment:</span> You understand that you have the right to
            refuse any treatment or procedure. However, you acknowledge that refusal may negatively affect your health
            outcomes and assume full responsibility for such decisions.</li>
        </ul>

        <div class="flex items-center space-x-4 mb-4 border-t-2 py-6">
          <input type="checkbox" id="consentForTreatment" v-model="agreements.consentForTreatment"
            class="form-checkbox h-5 w-5 text-custom-yellow border-2 border-custom-yellow focus:ring-custom-yellow">
          <label for="consentForTreatment" class="text-sm font-semibold">
            Consent for Treatment
          </label>
        </div>

        <!-- Financial Responsibility -->
        <h2 class="text-2xl font-bold mb-4">Financial Responsibility</h2>
        <p class="mb-4">You acknowledge and agree to the following terms regarding financial responsibility for the
          treatment provided:</p>
        <ol class="list-decimal pl-5 space-y-2 mb-4">
          <li><span class="font-semibold">Insurance Coverage:</span> You are responsible for providing accurate
            insurance information and cooperating in the claims submission process. You understand that insurance
            benefits vary, and not all services may be covered.</li>
          <li><span class="font-semibold">Non-Covered Services:</span> You agree to be financially responsible for any
            charges not covered by your insurance, including co-payments, deductibles, and services deemed non-essential
            or elective by your insurance provider.</li>
          <li><span class="font-semibold">Payment Obligations:</span> You agree to pay all balances in full for any
            charges not covered by your insurance immediately upon receiving a bill from the Healthcare Provider.</li>
          <li><span class="font-semibold">Collection of Unpaid Balances:</span> If payment is not made according to the
            agreed terms, you understand that unpaid balances may be referred to a collection agency, and additional
            fees may apply.</li>
        </ol>

        <div class="flex items-center space-x-4 mb-4 border-t-2 py-6">
          <input type="checkbox" id="consentFinancial" v-model="agreements.consentFinancial"
            class="form-checkbox h-5 w-5 text-custom-yellow border-2 border-custom-yellow focus:ring-custom-yellow">
          <label for="consentFinancial" class="text-sm font-semibold">
            Consent for Financial Responsibility
          </label>
        </div>

        <!-- Acknowledgement and Consent -->
        <h2 class="text-2xl font-bold mb-4">Acknowledgement and Consent</h2>
        <p class="mb-4">By signing this document, you acknowledge that:</p>
        <ol class="list-decimal pl-5 space-y-2 mb-4">
          <li>You have read and fully understand this form.</li>
          <li>You voluntarily consent to the collection, processing, and retention of your personal data as described
            above.</li>
          <li>You consent to receive medical treatment as recommended by your Healthcare Provider.</li>
          <li>You accept financial responsibility for your treatment and agree to pay for services not covered by your
            insurance or any other means.</li>
          <li>You understand your rights regarding the privacy of your medical and personal information and your right
            to revoke consent at any time, subject to the limits of the law.</li>
        </ol>

        <div class="flex items-center space-x-4 mb-4 border-t-2 py-6">
          <input type="checkbox" id="acknowledgementConsent" v-model="agreements.acknowledgementConsent"
            @change="toggleAllConsents"
            class="form-checkbox h-5 w-5 text-custom-yellow border-2 border-custom-yellow focus:ring-custom-yellow">
          <label for="acknowledgementConsent" class="text-sm font-semibold">
            Acknowledgement and Consent
          </label>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Patient or Legally Authorized Representative Name and Signature <span class="text-red-500">*</span>
          </label>
          <input type="text" v-model="agreements.fullName" placeholder="Enter Full Name"
            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Date <span class="text-red-500">*</span>
          </label>
          <input type="date" v-model="agreements.signatureDate"
            class="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50">
        </div>
      </div>

      <!-- Navigation buttons -->
      <div class="flex justify-end space-x-4 pl-4 ml-auto mt-4">
        <button v-if="currentStep > 1" @click="previousStep"
          class="text-custom-yellow border border-custom-yellow px-4 py-2 rounded-md hover:bg-custom-yellow hover:text-white focus:outline-none focus:ring-2 focus:ring-custom-yellow focus:ring-opacity-50">
          Back
        </button>
        <button v-if="currentStep < steps.length" @click="nextStep"
          class="bg-custom-yellow text-white px-4 py-2 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
          Next
        </button>
        <button v-if="currentStep === steps.length" @click="submitForm"
          class="bg-custom-yellow text-white px-4 py-2 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50">
          Submit
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import capitation from '@/assets/images/capitation.png';
import ffs from '@/assets/images/ffs.png';
import walkin from '@/assets/images/walkin.png';
import { debounce } from 'lodash';

export default {
  name: 'PatientRegistrationPageMobile',
  created() {
    // Create a debounced version of the lookupPatient method
    this.debouncedLookupPatient = debounce(this.lookupPatient, 300);
  },
  data() {
    return {
      currentStep: 1,
      steps: [
        'Personal Information',
        'Emergency Contact',
        'Payment Information',
        'Upload/Capture Referral Letter',
        'Consent for Data Collection, Processing and Retention'
      ],
      matchingPatients: [],
      showPatientPopup: false,
      debouncedLookupPatient: null,
      form: {
        title: '',
        fullName: '',
        dob: '',
        age: '',
        gender: '',
        relationship: '',
        idNumber: '',
        visitType: '',
        bloodGroup: '',
        phoneNumber: '',
        emailAddress: '',
        occupation: '',
        maritalStatus: '',
        residence: '',
        county: '',
        nationality: '',
      },
      emergencyContact: {
        fullName: '',
        relationship: '',
        phoneNumber: ''
      },
      emergencyContacts: [],
      uploadedDocuments: [],
      selectedMethod: 'walkin',
      selectedMobileProvider: 'Safaricom',
      paymentMethods: [
        { id: 'capitation', name: 'Capitation', url: capitation },
        { id: 'ffs', name: 'FFS', url: ffs },
        { id: 'walkin', name: 'Walk-In', url: walkin },
      ],
      agreements: {
        consentChecked: false,
        consentForTreatment: false,
        consentFinancial: false,
        acknowledgementConsent: false,
        fullName: '',
        signatureDate: ''
      },

    }
  },

  methods: {
    async lookupPatient() {
      // Only proceed if both fullName and phoneNumber have values
      if (!this.form.idNumber || !this.form.phoneNumber) {
        this.showPatientPopup = false;
        return;
      }

      try {
        // Fetch all patient data (replace with your actual API endpoint)
        const response = await fetch('/patient-details.json');
        const allPatientData = await response.json();

        // Filter patients based on fullName and phoneNumber
        this.matchingPatients = allPatientData.filter(patient =>
          patient.form.idNumber.includes(this.form.idNumber) &&
          patient.form.phoneNumber.includes(this.form.phoneNumber)
        );

        this.showPatientPopup = this.matchingPatients.length > 0;
      } catch (error) {
        console.error('Error looking up patient:', error);
      }
    },

    selectPatient(patient) {
      // Fill in the form with the selected patient's data
      Object.keys(this.form).forEach(key => {
        if (patient.form[key]) {
          this.form[key] = patient.form[key];
        }
      });

      // Fill in emergency contacts if available
      if (patient.emergencyContacts) {
        this.emergencyContacts = [...patient.emergencyContacts];
      }

      this.showPatientPopup = false;
    },

    submitForm() {
      // Combine all data into a single object
      const formData = {
        ...this.form,
        emergencyContact: this.emergencyContact,
        emergencyContacts: this.emergencyContacts,
        uploadedDocuments: this.uploadedDocuments,
        selectedMethod: this.selectedMethod,
        selectedMobileProvider: this.selectedMobileProvider,
        // paymentMethods: this.paymentMethods,
        agreements: this.agreements,
      };

      // Check if all required agreements are accepted
      const allAgreementsAccepted =
        this.agreements.consentForTreatment &&
        this.agreements.consentFinancial &&
        this.agreements.acknowledgementConsent;

      if (allAgreementsAccepted) {
        console.log('Form Data:', formData); // Log consolidated form data

        // Proceed with form submission logic, e.g., API call
        // Example:
        // axios.post('API_ENDPOINT', formData)
        //   .then(response => console.log('Form submitted successfully!', response))
        //   .catch(error => console.error('Error submitting form:', error));
      } else {
        alert('Please fill in all required fields and check all consent boxes.');
      }
    },
    toggleAllConsents() {
      const value = this.agreements.acknowledgementConsent;
      this.agreements.consentChecked = value;
      this.agreements.consentForTreatment = value;
      this.agreements.consentFinancial = value;
    },
    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    addEmergencyContact() {
      if (this.emergencyContact.fullName && this.emergencyContact.relationship && this.emergencyContact.phoneNumber) {
        this.emergencyContacts.push({ ...this.emergencyContact });
        this.emergencyContact = { fullName: '', relationship: '', phoneNumber: '' };
      } else {
        alert("Please fill in all fields before adding the contact.");
      }
    },
    deleteEmergencyContact(index) {
      // Remove the selected contact from the list
      this.emergencyContacts.splice(index, 1);
    },


    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.addDocuments(event.target.files);
    },
    handleFileDrop(event) {
      this.addDocuments(event.dataTransfer.files);
    },
    addDocuments(files) {
      for (let file of files) {
        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.uploadedDocuments.push({
              file: file,
              preview: e.target.result
            });
          };
          reader.readAsDataURL(file);
        } else {
          alert('Please upload only image or PDF files.');
        }
      }
    },
    removeDocument(index) {
      this.uploadedDocuments.splice(index, 1);
    }
  },
  watch: {
    'form.dob': function (newDob) {
      if (newDob) {
        const birthDate = new Date(newDob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        this.form.age = age.toString();
      } else {
        this.form.age = '';
      }
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>